import React from 'react';
import update from 'lodash.update';
import { create } from 'zustand';
import { devtools, subscribeWithSelector } from 'zustand/middleware';
import { shallow } from 'zustand/shallow';

import { cloneDeep } from '~/components/Header/lib/util/utils';

interface IState {
	cache: Record<string, any>;
	client: Record<string, any>;
	server: Record<string, any>;
}

interface IMethods {
	replaceClient: (val: any) => void;
	replaceServer: (val: any) => void;
	updateCache: (key: string, val: any) => void;
	updateClient: (key: string | string[], val: any) => void;
	updateServer: (key: string | string[], val: any) => void;
}

export type IStore = IState & IMethods;

const initialState: IState = {
	cache: {},
	client: {},
	server: {
		config: {
			baseUrl: '/us/',
			nextUpEvents: [],
			nextUpEventsRacing: [],
		},
		myoc: {
			isLoggedIn: false,
			deviceID: '2ea16976-1dd2-4a36-aa8d-f8a1762878e8',
		},
		_myoc: {
			isLoggedIn: true,
			deviceID: '2ea16976-1dd2-4a36-aa8d-f8a1762878e8',
			myOCUser: {
				userID: '1307300',
				name: '',
				email: 'glen.lawson@oddschecker.com',
				newsletterOptIn: false,
				bookieOfferOptIn: false,
				isPremium: true,
				premiumExpiry: '2050-09-21T20:00:00Z',
				premiumName: 'Annual All Access',
			},
		},
	},
};

// TODO: migrate to top level stores dir
const store = create<IStore>()(
	subscribeWithSelector(
		devtools((set, get) => ({
			...initialState,
			updateCache: (key: string, val: any) => {
				set(state => ({ cache: { ...state.cache, [key]: val } }));
			},
			// TODO: we should be using immer here rather than lodash
			// https://github.com/pmndrs/zustand#sick-of-reducers-and-changing-nested-states-use-immer
			updateClient: (key: string | string[], val: any) => {
				if (Array.isArray(key)) {
					set(state => {
						const client = cloneDeep(state.client);
						update(client, key.join('.'), () => val);

						return { client };
					});
				} else {
					set(state => ({ client: { ...state.client, [key]: val } }));
				}
			},
			updateServer: (key: string | string[], val: any) => {
				if (Array.isArray(key)) {
					set(state => {
						const server = cloneDeep(state.server);
						update(server, key.join('.'), () => val);

						return { server };
					});
				} else {
					set(state => ({ server: { ...state.server, [key]: val } }));
				}
			},
			replaceClient: (val: any) => set(state => ({ client: val })),
			replaceServer: (val: any) => set(state => ({ server: val })),
		})),
	),
);

// NOTE: we're unlikely to use this compound HOC in
// practice as it subscribes to all store updates
export const withStore = ({ children }: any) => {
	return React.Children.map(children, child => {
		const item = child as React.ReactElement;

		// Return item if regular DOM element
		if (typeof item.type === 'string') {
			return item;
		}

		// Return compound component
		return React.cloneElement(item, {
			store: store(),
		});
	});
};

export function withStoreSlices<T extends {} = IState>(
	Component: React.ComponentType<T>,
	paths: Record<string, any> = {},
) {
	const ComponentWithStore = (props: Omit<T, keyof IState>) => {
		const slices = useSlice(paths);

		return <Component {...(props as T)} {...slices} />;
	};

	// Ensure we assign a display name to support with debugging
	const displayName = Component.displayName || Component.name || 'Component';
	ComponentWithStore.displayName = `withStoreSlices(${displayName})`;

	return ComponentWithStore;
}

export const useSlice = (
	paths: Record<string, string[]>,
): Record<string, any> => {
	return store(
		state =>
			Object.entries(paths).reduce((slices, [key, paths]) => {
				try {
					// Construct key value pairs from store data
					const value = paths.reduce((s: any, p) => {
						return s[p];
					}, state);

					return { ...slices, [key]: value };
				} catch (e) {
					return { ...slices, [key]: undefined };
				}
			}, {}),
		shallow,
	);
};

export default store;
